'use client'

import { useState } from 'react'
import type { Dispatch, KeyboardEvent, SetStateAction } from 'react'
import { useForm } from '@tanstack/react-form'
import { useStore } from '@tanstack/react-store'
import { ArrowLeft, ArrowRight, Loader2, Shield, X } from 'lucide-react'

import { CANDIDATE_STATUS_LABELS } from '@/app/(webinar-form)/webinar/webinar-copy'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { EventRegistrationCandidateStatus } from '@/lib/event/registration-options'
import { EVENT_REGISTRATION_CANDIDATE_STATUSES } from '@/lib/event/registration-options'

import type { talentPoolCopy } from '@/app/talent-pool/copy'
import {
  formatIdrAmount,
  sanitizeNumericInput,
} from '@/app/talent-pool/salary-range'
import type { TalentPoolFormDraft } from '@/app/talent-pool/talent-pool-store'
import { trackEvent } from '@/lib/analytics'

export type TalentPoolFormStep = 'personal' | 'preferences' | 'submit'
type TalentPoolFormGtmProps = Record<string, string | number | boolean | null>

const pushGtmEvent = (event: string, properties?: TalentPoolFormGtmProps) => {
  if (typeof window === 'undefined') return
  trackEvent(event, properties)
}

type TalentPoolFormProps = {
  step: TalentPoolFormStep
  copy: typeof talentPoolCopy
  draft: TalentPoolFormDraft
  fileName: string | null
  fieldErrors: Record<string, string>
  submitError: string
  isSubmitting: boolean
  isAuthenticating: boolean
  onBack: () => void
  onNext: () => void
  onSubmit: () => void
  setDraft: (partial: Partial<TalentPoolFormDraft>) => void
  setFieldErrors: Dispatch<SetStateAction<Record<string, string>>>
}

export function TalentPoolForm({
  step,
  copy,
  draft,
  fileName,
  fieldErrors,
  submitError,
  isSubmitting,
  isAuthenticating,
  onBack,
  onNext,
  onSubmit,
  setDraft,
  setFieldErrors,
}: TalentPoolFormProps) {
  const [roleInput, setRoleInput] = useState('')

  const form = useForm({
    defaultValues: {
      fullName: draft.fullName,
      emailAddress: draft.emailAddress,
      phoneNumber: draft.phoneNumber,
      birthDate: draft.birthDate,
      targetedRoles: draft.targetedRoles,
      expectedSalaryMin: draft.expectedSalaryMin,
      currentCity: draft.currentCity,
      willingToRelocate: draft.willingToRelocate,
      status: draft.status,
    },
  })
  const values = useStore(form.store, (state) => state.values)

  const clearFieldError = (field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev
      return { ...prev, [field]: '' }
    })
  }

  const syncDraft = <K extends keyof TalentPoolFormDraft>(
    key: K,
    value: TalentPoolFormDraft[K],
  ) => {
    setDraft({ [key]: value } as Partial<TalentPoolFormDraft>)
  }

  const formatExpectedSalary = (value: string) => {
    const numeric = sanitizeNumericInput(value)
    if (!numeric) return ''
    return `Rp ${formatIdrAmount(numeric)}`
  }

  const syncExpectedSalary = (value: string) => {
    const expectedSalary = sanitizeNumericInput(value)
    setDraft({
      expectedSalaryMin: expectedSalary,
      expectedSalaryMax: '',
      expectedSalaryRange: formatExpectedSalary(expectedSalary),
    })
    clearFieldError('expectedSalaryRange')
  }

  const addRole = (rawValue: string) => {
    const trimmed = rawValue.trim()
    if (!trimmed) return
    if (values.targetedRoles.length >= 3) return
    if (values.targetedRoles.includes(trimmed)) return

    const nextRoles = [...values.targetedRoles, trimmed]
    form.setFieldValue('targetedRoles', nextRoles)
    setDraft({ targetedRoles: nextRoles })
    clearFieldError('targetedRoles')
  }

  const removeRole = (index: number) => {
    const nextRoles = values.targetedRoles.filter((_, i) => i !== index)
    form.setFieldValue('targetedRoles', nextRoles)
    setDraft({ targetedRoles: nextRoles })
  }

  const handleRoleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addRole(roleInput)
      setRoleInput('')
    }
  }

  const canGoToPreferences =
    values.fullName.trim().length > 0 &&
    values.emailAddress.trim().length > 0 &&
    values.phoneNumber.trim().length > 0 &&
    values.birthDate.trim().length > 0
  const canGoToSubmit =
    values.targetedRoles.length > 0 &&
    values.expectedSalaryMin.trim().length > 0 &&
    values.currentCity.trim().length > 0 &&
    values.willingToRelocate.length > 0 &&
    values.status.length > 0
  const salarySummary =
    formatExpectedSalary(values.expectedSalaryMin) ||
    draft.expectedSalaryRange
  const baseTrackingProps = {
    page: 'talent-pool',
    placement: 'form',
    step,
  } as const

  const trackBackClick = () => {
    pushGtmEvent('talent_pool_form_back_click', baseTrackingProps)

    if (step === 'personal') {
      pushGtmEvent('talent_pool_form_personal_back_click', baseTrackingProps)
      return
    }

    if (step === 'preferences') {
      pushGtmEvent(
        'talent_pool_form_preferences_back_click',
        baseTrackingProps,
      )
      return
    }

    pushGtmEvent('talent_pool_form_submit_back_click', baseTrackingProps)
  }

  const trackNextClick = (fromStep: 'personal' | 'preferences') => {
    pushGtmEvent('talent_pool_form_next_click', {
      ...baseTrackingProps,
      from_step: fromStep,
    })

    if (fromStep === 'personal') {
      pushGtmEvent('talent_pool_form_personal_next_click', baseTrackingProps)
      return
    }

    pushGtmEvent('talent_pool_form_preferences_next_click', baseTrackingProps)
  }

  const trackSubmitClick = () => {
    pushGtmEvent('talent_pool_form_submit_click', baseTrackingProps)
  }

  if (step === 'personal') {
    return (
      <section className="space-y-6">
        <div>
          <h2 className="text-[24px] font-semibold tracking-[-0.02em] text-[#222222]">
            {copy.personal.title}
          </h2>
          <p className="mt-1 text-[14px] text-[#6d6d6d]">{copy.personal.subtitle}</p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[#6f6f6f]">
              {copy.personal.fields.fullName.label}
            </Label>
            <form.Field name="fullName">
              {(field) => (
                <Input
                  value={field.state.value}
                  onChange={(e) => {
                    const next = e.target.value
                    field.handleChange(next)
                    syncDraft('fullName', next)
                    clearFieldError('fullName')
                  }}
                  placeholder={copy.personal.fields.fullName.placeholder}
                  className={`h-12 bg-[#fcfcfc] text-[15px] ${
                    fieldErrors.fullName ? 'border-brand-primary' : 'border-[#d7d7d7]'
                  }`}
                />
              )}
            </form.Field>
            {fieldErrors.fullName && (
              <p className="text-[12px] text-brand-primary">{fieldErrors.fullName}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[#6f6f6f]">
              {copy.personal.fields.emailAddress.label}
            </Label>
            <form.Field name="emailAddress">
              {(field) => (
                <Input
                  type="email"
                  inputMode="email"
                  value={field.state.value}
                  onChange={(e) => {
                    const next = e.target.value
                    field.handleChange(next)
                    syncDraft('emailAddress', next)
                    clearFieldError('emailAddress')
                  }}
                  placeholder={copy.personal.fields.emailAddress.placeholder}
                  className={`h-12 bg-[#fcfcfc] text-[15px] ${
                    fieldErrors.emailAddress ? 'border-brand-primary' : 'border-[#d7d7d7]'
                  }`}
                />
              )}
            </form.Field>
            {fieldErrors.emailAddress && (
              <p className="text-[12px] text-brand-primary">{fieldErrors.emailAddress}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[#6f6f6f]">
              {copy.personal.fields.phoneNumber.label}
            </Label>
            <form.Field name="phoneNumber">
              {(field) => (
                <Input
                  type="tel"
                  inputMode="tel"
                  value={field.state.value}
                  onChange={(e) => {
                    const next = e.target.value
                    field.handleChange(next)
                    syncDraft('phoneNumber', next)
                    clearFieldError('phoneNumber')
                  }}
                  placeholder={copy.personal.fields.phoneNumber.placeholder}
                  className={`h-12 bg-[#fcfcfc] text-[15px] ${
                    fieldErrors.phoneNumber ? 'border-brand-primary' : 'border-[#d7d7d7]'
                  }`}
                />
              )}
            </form.Field>
            {fieldErrors.phoneNumber && (
              <p className="text-[12px] text-brand-primary">{fieldErrors.phoneNumber}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[#6f6f6f]">
              {copy.personal.fields.birthDate.label}
            </Label>
            <form.Field name="birthDate">
              {(field) => (
                <Input
                  type="date"
                  value={field.state.value}
                  onChange={(e) => {
                    const next = e.target.value
                    field.handleChange(next)
                    syncDraft('birthDate', next)
                    clearFieldError('birthDate')
                  }}
                  className={`h-12 bg-[#fcfcfc] text-[15px] ${
                    fieldErrors.birthDate ? 'border-brand-primary' : 'border-[#d7d7d7]'
                  }`}
                />
              )}
            </form.Field>
            {fieldErrors.birthDate && (
              <p className="text-[12px] text-brand-primary">{fieldErrors.birthDate}</p>
            )}
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              trackBackClick()
              onBack()
            }}
            className="h-12 flex-1 rounded-xl border-[#d8d8d8] text-[#303030]"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {copy.common.back}
          </Button>
          <Button
            type="button"
            disabled={!canGoToPreferences}
            onClick={() => {
              trackNextClick('personal')
              onNext()
            }}
            className="h-12 flex-1 rounded-xl bg-brand-primary text-[16px] font-semibold text-white hover:bg-brand-primary-hover"
          >
            {copy.common.next}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>
    )
  }

  if (step === 'preferences') {
    return (
      <section className="space-y-6">
        <div>
          <h2 className="text-[24px] font-semibold tracking-[-0.02em] text-[#222222]">
            {copy.preferences.title}
          </h2>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[#6f6f6f]">
              {copy.preferences.fields.targetedRoles.label}
            </Label>
            <div className="rounded-lg border border-[#d7d7d7] bg-[#fcfcfc] p-2">
              <div className="flex flex-wrap gap-2">
                {values.targetedRoles.map((role, i) => (
                  <span
                    key={`${role}-${i}`}
                    className="inline-flex items-center gap-1 rounded-full bg-brand-primary/10 px-3 py-1 text-[13px] font-medium text-brand-primary"
                  >
                    {role}
                    <button
                      type="button"
                      onClick={() => removeRole(i)}
                      className="ml-0.5 rounded-full p-0.5 hover:bg-brand-primary/15"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                {values.targetedRoles.length < 3 && (
                  <Input
                    value={roleInput}
                    onChange={(e) => setRoleInput(e.target.value)}
                    onKeyDown={handleRoleKeyDown}
                    onBlur={() => {
                      if (roleInput.trim()) {
                        addRole(roleInput)
                        setRoleInput('')
                      }
                    }}
                    placeholder={copy.preferences.fields.targetedRoles.placeholder}
                    className="h-8 min-w-[180px] flex-1 border-0 bg-transparent p-0 text-[14px] shadow-none focus-visible:ring-0"
                  />
                )}
              </div>
            </div>
            <p className="text-[11px] text-[#999999]">
              {copy.preferences.fields.targetedRoles.hint}
            </p>
            {fieldErrors.targetedRoles && (
              <p className="text-[12px] text-brand-primary">{fieldErrors.targetedRoles}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[#6f6f6f]">
              {copy.preferences.fields.expectedSalaryRange.label}
            </Label>
            <form.Field name="expectedSalaryMin">
              {(field) => (
                <Input
                  value={formatIdrAmount(field.state.value)}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onChange={(e) => {
                    const next = sanitizeNumericInput(e.target.value)
                    field.handleChange(next)
                    syncExpectedSalary(next)
                  }}
                  placeholder={copy.preferences.fields.expectedSalaryRange.placeholder}
                  className={`h-12 bg-[#fcfcfc] text-[15px] ${
                    fieldErrors.expectedSalaryRange
                      ? 'border-brand-primary'
                      : 'border-[#d7d7d7]'
                  }`}
                />
              )}
            </form.Field>
            {fieldErrors.expectedSalaryRange && (
              <p className="text-[12px] text-brand-primary">{fieldErrors.expectedSalaryRange}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[#6f6f6f]">
              {copy.preferences.fields.currentCity.label}
            </Label>
            <form.Field name="currentCity">
              {(field) => (
                <Input
                  value={field.state.value}
                  onChange={(e) => {
                    const next = e.target.value
                    field.handleChange(next)
                    syncDraft('currentCity', next)
                    clearFieldError('currentCity')
                  }}
                  placeholder={copy.preferences.fields.currentCity.placeholder}
                  className={`h-12 bg-[#fcfcfc] text-[15px] ${
                    fieldErrors.currentCity ? 'border-brand-primary' : 'border-[#d7d7d7]'
                  }`}
                />
              )}
            </form.Field>
            {fieldErrors.currentCity && (
              <p className="text-[12px] text-brand-primary">{fieldErrors.currentCity}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[#6f6f6f]">
              {copy.preferences.fields.willingToRelocate.label}
            </Label>
            <form.Field name="willingToRelocate">
              {(field) => (
                <Select
                  value={field.state.value}
                  onValueChange={(value: 'Yes' | 'No') => {
                    field.handleChange(value)
                    syncDraft('willingToRelocate', value)
                    clearFieldError('willingToRelocate')
                  }}
                >
                  <SelectTrigger
                    className={`h-12 bg-[#fcfcfc] text-left text-[15px] ${
                      fieldErrors.willingToRelocate
                        ? 'border-brand-primary'
                        : 'border-[#d7d7d7]'
                    }`}
                  >
                    <SelectValue
                      placeholder={copy.preferences.fields.willingToRelocate.placeholder}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {copy.preferences.fields.willingToRelocate.options.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </form.Field>
            {fieldErrors.willingToRelocate && (
              <p className="text-[12px] text-brand-primary">{fieldErrors.willingToRelocate}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[#6f6f6f]">
              {copy.preferences.fields.status.label}
            </Label>
            <form.Field name="status">
              {(field) => (
                <Select
                  value={field.state.value}
                  onValueChange={(value: EventRegistrationCandidateStatus) => {
                    field.handleChange(value)
                    syncDraft('status', value)
                    clearFieldError('status')
                  }}
                >
                  <SelectTrigger
                    className={`h-12 bg-[#fcfcfc] text-left text-[15px] ${
                      fieldErrors.status ? 'border-brand-primary' : 'border-[#d7d7d7]'
                    }`}
                  >
                    <SelectValue placeholder={copy.preferences.fields.status.placeholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_REGISTRATION_CANDIDATE_STATUSES.map((statusValue) => (
                      <SelectItem key={statusValue} value={statusValue}>
                        {CANDIDATE_STATUS_LABELS.id[statusValue]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </form.Field>
            {fieldErrors.status && (
              <p className="text-[12px] text-brand-primary">{fieldErrors.status}</p>
            )}
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              trackBackClick()
              onBack()
            }}
            className="h-12 flex-1 rounded-xl border-[#d8d8d8] text-[#303030]"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {copy.common.back}
          </Button>
          <Button
            type="button"
            disabled={!canGoToSubmit}
            onClick={() => {
              trackNextClick('preferences')
              onNext()
            }}
            className="h-12 flex-1 rounded-xl bg-brand-primary text-[16px] font-semibold text-white hover:bg-brand-primary-hover"
          >
            {copy.common.next}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>
    )
  }

  return (
    <section className="space-y-6">
      <div>
        <h2 className="text-[24px] font-semibold tracking-[-0.02em] text-[#222222]">
          {copy.submit.title}
        </h2>
      </div>

      <div className="space-y-3 rounded-2xl border border-[#e5e5e5] bg-white p-5 shadow-[0_6px_16px_rgba(0,0,0,0.05)]">
        <h3 className="text-[16px] font-semibold text-[#222222]">{copy.submit.summaryTitle}</h3>
        <dl className="space-y-2 text-[14px]">
          <div className="flex justify-between gap-4">
            <dt className="text-[#7a7a7a]">{copy.submit.labels.cv}</dt>
            <dd className="text-right font-medium text-[#222222]">
              {fileName ?? copy.submit.notFilled}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-[#7a7a7a]">{copy.submit.labels.fullName}</dt>
            <dd className="text-right font-medium text-[#222222]">
              {values.fullName || copy.submit.notFilled}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-[#7a7a7a]">{copy.submit.labels.emailAddress}</dt>
            <dd className="text-right font-medium text-[#222222]">
              {values.emailAddress || copy.submit.notFilled}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-[#7a7a7a]">{copy.submit.labels.phoneNumber}</dt>
            <dd className="text-right font-medium text-[#222222]">
              {values.phoneNumber || copy.submit.notFilled}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-[#7a7a7a]">{copy.submit.labels.birthDate}</dt>
            <dd className="text-right font-medium text-[#222222]">
              {values.birthDate || copy.submit.notFilled}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-[#7a7a7a]">{copy.submit.labels.targetedRoles}</dt>
            <dd className="text-right font-medium text-[#222222]">
              {values.targetedRoles.length > 0
                ? values.targetedRoles.join(', ')
                : copy.submit.notFilled}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-[#7a7a7a]">{copy.submit.labels.expectedSalaryRange}</dt>
            <dd className="text-right font-medium text-[#222222]">
              {salarySummary || copy.submit.notFilled}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-[#7a7a7a]">{copy.submit.labels.currentCity}</dt>
            <dd className="text-right font-medium text-[#222222]">
              {values.currentCity || copy.submit.notFilled}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-[#7a7a7a]">{copy.submit.labels.willingToRelocate}</dt>
            <dd className="text-right font-medium text-[#222222]">
              {values.willingToRelocate || copy.submit.notSelected}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-[#7a7a7a]">{copy.submit.labels.status}</dt>
            <dd className="text-right font-medium text-[#222222]">
              {values.status
                ? CANDIDATE_STATUS_LABELS.id[values.status]
                : copy.submit.notSelected}
            </dd>
          </div>
        </dl>
      </div>

      <div className="flex items-start gap-2 rounded-xl bg-[#f8f8f8] p-3">
        <Shield className="mt-0.5 h-4 w-4 text-[#999999]" />
        <p className="text-[12px] leading-[1.6] text-[#7a7a7a]">{copy.submit.privacyNote}</p>
      </div>

      {submitError && <p className="text-center text-[13px] text-brand-primary">{submitError}</p>}

      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            trackBackClick()
            onBack()
          }}
          className="h-12 flex-1 rounded-xl border-[#d8d8d8] text-[#303030]"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {copy.common.back}
        </Button>
        <Button
          type="button"
          disabled={isSubmitting || isAuthenticating}
          onClick={() => {
            trackSubmitClick()
            onSubmit()
          }}
          className="h-12 flex-1 rounded-xl bg-brand-primary text-[13px] font-semibold text-white hover:bg-brand-primary-hover sm:text-[16px]"
        >
          {isSubmitting || isAuthenticating ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="truncate">{isAuthenticating ? 'Login...' : copy.submit.submitting}</span>
            </span>
          ) : (
            <span className="truncate">{copy.submit.loginButton}</span>
          )}
        </Button>
      </div>
    </section>
  )
}
